// Extended atomic dataset for the ATOMIC/PERIODIC cluster widgets. Layers the
// college-level properties the trend overlays + detail panels need on TOP of the
// shared ELEMENTS table in ../elements.ts (we never duplicate Z/symbol/name/mass/
// category/group/period — those come from there). Self-contained, no network.
//
// Property sources are standard general-chemistry reference values:
//   • electronegativity  — Pauling scale (null = not meaningfully defined, e.g. noble gases w/o a value)
//   • atomicRadius       — empirical/covalent radius in picometres (pm)
//   • ionizationEnergy   — first ionization energy in kJ/mol
//   • electronAffinity   — in kJ/mol (more negative ⇒ more favourable; sign = energy released convention here as negative)
//   • meltingPoint       — in kelvin (K); null when not applicable
//   • block              — s | p | d | f
//   • oxidationStates    — common oxidation states (most common first-ish)
//   • electronConfig     — full ground-state configuration as ordered subshell tokens
//
// Coverage: full property rows for Z 1–54 (the brief's floor), with the lighter
// "everything-derivable" fields (block, electron config) computable for ALL 118
// via the aufbau helpers below so the configuration/aufbau widget works table-wide.

import { ELEMENTS, BY_Z, type ElementInfo, type ElementCategory } from '../elements';

export type Block = 's' | 'p' | 'd' | 'f';

export interface AtomicProps {
  electronegativity: number | null; // Pauling
  atomicRadius: number | null; // pm
  ionizationEnergy: number | null; // kJ/mol
  electronAffinity: number | null; // kJ/mol (negative = exothermic)
  meltingPoint: number | null; // K
  block: Block;
  oxidationStates: number[];
}

// Hand-curated property table for Z 1–54. Keyed by Z.
// prettier-ignore
const PROPS: Record<number, AtomicProps> = {
  1:  { electronegativity: 2.20, atomicRadius: 53,  ionizationEnergy: 1312, electronAffinity: -73,  meltingPoint: 14,    block: 's', oxidationStates: [1, -1] },
  2:  { electronegativity: null, atomicRadius: 31,  ionizationEnergy: 2372, electronAffinity: 0,    meltingPoint: 1,     block: 's', oxidationStates: [0] },
  3:  { electronegativity: 0.98, atomicRadius: 167, ionizationEnergy: 520,  electronAffinity: -60,  meltingPoint: 454,   block: 's', oxidationStates: [1] },
  4:  { electronegativity: 1.57, atomicRadius: 112, ionizationEnergy: 899,  electronAffinity: 0,    meltingPoint: 1560,  block: 's', oxidationStates: [2] },
  5:  { electronegativity: 2.04, atomicRadius: 87,  ionizationEnergy: 801,  electronAffinity: -27,  meltingPoint: 2349,  block: 'p', oxidationStates: [3] },
  6:  { electronegativity: 2.55, atomicRadius: 67,  ionizationEnergy: 1086, electronAffinity: -122, meltingPoint: 3823,  block: 'p', oxidationStates: [4, -4, 2] },
  7:  { electronegativity: 3.04, atomicRadius: 56,  ionizationEnergy: 1402, electronAffinity: 7,    meltingPoint: 63,    block: 'p', oxidationStates: [-3, 5, 3, -3] },
  8:  { electronegativity: 3.44, atomicRadius: 48,  ionizationEnergy: 1314, electronAffinity: -141, meltingPoint: 54,    block: 'p', oxidationStates: [-2] },
  9:  { electronegativity: 3.98, atomicRadius: 42,  ionizationEnergy: 1681, electronAffinity: -328, meltingPoint: 53,    block: 'p', oxidationStates: [-1] },
  10: { electronegativity: null, atomicRadius: 38,  ionizationEnergy: 2081, electronAffinity: 0,    meltingPoint: 25,    block: 'p', oxidationStates: [0] },
  11: { electronegativity: 0.93, atomicRadius: 190, ionizationEnergy: 496,  electronAffinity: -53,  meltingPoint: 371,   block: 's', oxidationStates: [1] },
  12: { electronegativity: 1.31, atomicRadius: 145, ionizationEnergy: 738,  electronAffinity: 0,    meltingPoint: 923,   block: 's', oxidationStates: [2] },
  13: { electronegativity: 1.61, atomicRadius: 118, ionizationEnergy: 578,  electronAffinity: -43,  meltingPoint: 933,   block: 'p', oxidationStates: [3] },
  14: { electronegativity: 1.90, atomicRadius: 111, ionizationEnergy: 786,  electronAffinity: -134, meltingPoint: 1687,  block: 'p', oxidationStates: [4, -4] },
  15: { electronegativity: 2.19, atomicRadius: 98,  ionizationEnergy: 1012, electronAffinity: -72,  meltingPoint: 317,   block: 'p', oxidationStates: [5, 3, -3] },
  16: { electronegativity: 2.58, atomicRadius: 88,  ionizationEnergy: 1000, electronAffinity: -200, meltingPoint: 388,   block: 'p', oxidationStates: [-2, 4, 6] },
  17: { electronegativity: 3.16, atomicRadius: 79,  ionizationEnergy: 1251, electronAffinity: -349, meltingPoint: 172,   block: 'p', oxidationStates: [-1, 1, 3, 5, 7] },
  18: { electronegativity: null, atomicRadius: 71,  ionizationEnergy: 1521, electronAffinity: 0,    meltingPoint: 84,    block: 'p', oxidationStates: [0] },
  19: { electronegativity: 0.82, atomicRadius: 243, ionizationEnergy: 419,  electronAffinity: -48,  meltingPoint: 337,   block: 's', oxidationStates: [1] },
  20: { electronegativity: 1.00, atomicRadius: 194, ionizationEnergy: 590,  electronAffinity: -2,   meltingPoint: 1115,  block: 's', oxidationStates: [2] },
  21: { electronegativity: 1.36, atomicRadius: 184, ionizationEnergy: 633,  electronAffinity: -18,  meltingPoint: 1814,  block: 'd', oxidationStates: [3] },
  22: { electronegativity: 1.54, atomicRadius: 176, ionizationEnergy: 659,  electronAffinity: -8,   meltingPoint: 1941,  block: 'd', oxidationStates: [4, 3, 2] },
  23: { electronegativity: 1.63, atomicRadius: 171, ionizationEnergy: 651,  electronAffinity: -51,  meltingPoint: 2183,  block: 'd', oxidationStates: [5, 4, 3, 2] },
  24: { electronegativity: 1.66, atomicRadius: 166, ionizationEnergy: 653,  electronAffinity: -64,  meltingPoint: 2180,  block: 'd', oxidationStates: [3, 6, 2] },
  25: { electronegativity: 1.55, atomicRadius: 161, ionizationEnergy: 717,  electronAffinity: 0,    meltingPoint: 1519,  block: 'd', oxidationStates: [2, 4, 7, 3, 6] },
  26: { electronegativity: 1.83, atomicRadius: 156, ionizationEnergy: 762,  electronAffinity: -16,  meltingPoint: 1811,  block: 'd', oxidationStates: [3, 2] },
  27: { electronegativity: 1.88, atomicRadius: 152, ionizationEnergy: 760,  electronAffinity: -64,  meltingPoint: 1768,  block: 'd', oxidationStates: [2, 3] },
  28: { electronegativity: 1.91, atomicRadius: 149, ionizationEnergy: 737,  electronAffinity: -112, meltingPoint: 1728,  block: 'd', oxidationStates: [2, 3] },
  29: { electronegativity: 1.90, atomicRadius: 145, ionizationEnergy: 745,  electronAffinity: -118, meltingPoint: 1358,  block: 'd', oxidationStates: [2, 1] },
  30: { electronegativity: 1.65, atomicRadius: 142, ionizationEnergy: 906,  electronAffinity: 0,    meltingPoint: 693,   block: 'd', oxidationStates: [2] },
  31: { electronegativity: 1.81, atomicRadius: 136, ionizationEnergy: 579,  electronAffinity: -29,  meltingPoint: 303,   block: 'p', oxidationStates: [3] },
  32: { electronegativity: 2.01, atomicRadius: 125, ionizationEnergy: 762,  electronAffinity: -119, meltingPoint: 1211,  block: 'p', oxidationStates: [4, 2] },
  33: { electronegativity: 2.18, atomicRadius: 114, ionizationEnergy: 947,  electronAffinity: -78,  meltingPoint: 1090,  block: 'p', oxidationStates: [3, 5, -3] },
  34: { electronegativity: 2.55, atomicRadius: 103, ionizationEnergy: 941,  electronAffinity: -195, meltingPoint: 494,   block: 'p', oxidationStates: [-2, 4, 6] },
  35: { electronegativity: 2.96, atomicRadius: 94,  ionizationEnergy: 1140, electronAffinity: -325, meltingPoint: 266,   block: 'p', oxidationStates: [-1, 1, 5] },
  36: { electronegativity: 3.00, atomicRadius: 88,  ionizationEnergy: 1351, electronAffinity: 0,    meltingPoint: 116,   block: 'p', oxidationStates: [0, 2] },
  37: { electronegativity: 0.82, atomicRadius: 265, ionizationEnergy: 403,  electronAffinity: -47,  meltingPoint: 312,   block: 's', oxidationStates: [1] },
  38: { electronegativity: 0.95, atomicRadius: 219, ionizationEnergy: 549,  electronAffinity: -5,   meltingPoint: 1050,  block: 's', oxidationStates: [2] },
  39: { electronegativity: 1.22, atomicRadius: 212, ionizationEnergy: 600,  electronAffinity: -30,  meltingPoint: 1799,  block: 'd', oxidationStates: [3] },
  40: { electronegativity: 1.33, atomicRadius: 206, ionizationEnergy: 640,  electronAffinity: -41,  meltingPoint: 2128,  block: 'd', oxidationStates: [4] },
  41: { electronegativity: 1.60, atomicRadius: 198, ionizationEnergy: 652,  electronAffinity: -86,  meltingPoint: 2750,  block: 'd', oxidationStates: [5, 3] },
  42: { electronegativity: 2.16, atomicRadius: 190, ionizationEnergy: 684,  electronAffinity: -72,  meltingPoint: 2896,  block: 'd', oxidationStates: [6, 4] },
  43: { electronegativity: 1.90, atomicRadius: 183, ionizationEnergy: 702,  electronAffinity: -53,  meltingPoint: 2430,  block: 'd', oxidationStates: [7, 4] },
  44: { electronegativity: 2.20, atomicRadius: 178, ionizationEnergy: 710,  electronAffinity: -101, meltingPoint: 2607,  block: 'd', oxidationStates: [4, 3, 8] },
  45: { electronegativity: 2.28, atomicRadius: 173, ionizationEnergy: 720,  electronAffinity: -110, meltingPoint: 2237,  block: 'd', oxidationStates: [3] },
  46: { electronegativity: 2.20, atomicRadius: 169, ionizationEnergy: 804,  electronAffinity: -54,  meltingPoint: 1828,  block: 'd', oxidationStates: [2, 4] },
  47: { electronegativity: 1.93, atomicRadius: 165, ionizationEnergy: 731,  electronAffinity: -126, meltingPoint: 1235,  block: 'd', oxidationStates: [1] },
  48: { electronegativity: 1.69, atomicRadius: 161, ionizationEnergy: 868,  electronAffinity: 0,    meltingPoint: 594,   block: 'd', oxidationStates: [2] },
  49: { electronegativity: 1.78, atomicRadius: 156, ionizationEnergy: 558,  electronAffinity: -29,  meltingPoint: 430,   block: 'p', oxidationStates: [3] },
  50: { electronegativity: 1.96, atomicRadius: 145, ionizationEnergy: 709,  electronAffinity: -107, meltingPoint: 505,   block: 'p', oxidationStates: [4, 2] },
  51: { electronegativity: 2.05, atomicRadius: 133, ionizationEnergy: 834,  electronAffinity: -103, meltingPoint: 904,   block: 'p', oxidationStates: [3, 5, -3] },
  52: { electronegativity: 2.10, atomicRadius: 123, ionizationEnergy: 869,  electronAffinity: -190, meltingPoint: 723,   block: 'p', oxidationStates: [-2, 4, 6] },
  53: { electronegativity: 2.66, atomicRadius: 115, ionizationEnergy: 1008, electronAffinity: -295, meltingPoint: 387,   block: 'p', oxidationStates: [-1, 1, 5, 7] },
  54: { electronegativity: 2.60, atomicRadius: 108, ionizationEnergy: 1170, electronAffinity: 0,    meltingPoint: 161,   block: 'p', oxidationStates: [0, 2, 4, 6] },
};

// ── Aufbau / electron-configuration engine (works for ALL Z, 1–118) ───────────

// Subshell capacities.
const SUBSHELL_CAP: Record<string, number> = { s: 2, p: 6, d: 10, f: 14 };

// Madelung (n + ℓ) diagonal-rule filling order, as [n, subshell] pairs. Covers
// through 7p which is enough for the whole table.
export const AUFBAU_ORDER: Array<{ n: number; sub: 's' | 'p' | 'd' | 'f' }> = [
  { n: 1, sub: 's' },
  { n: 2, sub: 's' }, { n: 2, sub: 'p' },
  { n: 3, sub: 's' }, { n: 3, sub: 'p' },
  { n: 4, sub: 's' }, { n: 3, sub: 'd' }, { n: 4, sub: 'p' },
  { n: 5, sub: 's' }, { n: 4, sub: 'd' }, { n: 5, sub: 'p' },
  { n: 6, sub: 's' }, { n: 4, sub: 'f' }, { n: 5, sub: 'd' }, { n: 6, sub: 'p' },
  { n: 7, sub: 's' }, { n: 5, sub: 'f' }, { n: 6, sub: 'd' }, { n: 7, sub: 'p' },
];

export interface SubshellFill {
  n: number;
  sub: 's' | 'p' | 'd' | 'f';
  count: number; // electrons in this subshell
  label: string; // e.g. "2p"
}

// Build the ground-state configuration for `z` electrons using the strict
// aufbau (Madelung) order. This is the teaching model: it reproduces the
// "expected" configuration (real anomalies like Cr/Cu are handled separately
// in ANOMALIES so the widget can flag them).
export function aufbauFill(z: number): SubshellFill[] {
  const fills: SubshellFill[] = [];
  let remaining = Math.max(0, Math.floor(z));
  for (const { n, sub } of AUFBAU_ORDER) {
    if (remaining <= 0) break;
    const cap = SUBSHELL_CAP[sub];
    const count = Math.min(cap, remaining);
    fills.push({ n, sub, count, label: `${n}${sub}` });
    remaining -= count;
  }
  return fills;
}

// Well-known exceptions to the strict aufbau order (the ones every gen-chem
// course calls out). Stored as the FULL corrected subshell map keyed by label.
// We only need the d/s swap subshells; everything else matches aufbau.
const ANOMALIES: Record<number, Record<string, number>> = {
  24: { '4s': 1, '3d': 5 }, // Cr: half-filled d stability
  29: { '4s': 1, '3d': 10 }, // Cu: filled d stability
  41: { '5s': 1, '4d': 4 }, // Nb
  42: { '5s': 1, '4d': 5 }, // Mo
  44: { '5s': 1, '4d': 7 }, // Ru
  45: { '5s': 1, '4d': 8 }, // Rh
  46: { '5s': 0, '4d': 10 }, // Pd
  47: { '5s': 1, '4d': 10 }, // Ag
  78: { '6s': 1, '5d': 9 }, // Pt
  79: { '6s': 1, '5d': 10 }, // Au
};

// True ground-state configuration: aufbau, then patched for known anomalies.
export function groundStateFill(z: number): { fills: SubshellFill[]; anomalous: boolean } {
  const fills = aufbauFill(z);
  const patch = ANOMALIES[z];
  if (!patch) return { fills, anomalous: false };
  const patched = fills.map((f) =>
    f.label in patch ? { ...f, count: patch[f.label] } : f,
  );
  return { fills: patched.filter((f) => f.count > 0), anomalous: true };
}

// Noble-gas core boundaries: [Z, symbol]. Used for shorthand notation.
const NOBLE_GASES: Array<{ z: number; symbol: string }> = [
  { z: 2, symbol: 'He' },
  { z: 10, symbol: 'Ne' },
  { z: 18, symbol: 'Ar' },
  { z: 36, symbol: 'Kr' },
  { z: 54, symbol: 'Xe' },
  { z: 86, symbol: 'Rn' },
];

// Render a fill list to spdf string, e.g. "1s² 2s² 2p⁶".
const SUPER: Record<string, string> = {
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
  '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
};
export function toSuperscript(n: number): string {
  return String(n).split('').map((d) => SUPER[d] ?? d).join('');
}

export function fillsToNotation(fills: SubshellFill[]): string {
  return fills.map((f) => `${f.label}${toSuperscript(f.count)}`).join(' ');
}

// Noble-gas shorthand: replace the leading subshells belonging to the largest
// noble-gas core ≤ z with [Ng], then the remaining valence subshells.
export function nobleGasShorthand(z: number): { core: string | null; valence: string } {
  const { fills } = groundStateFill(z);
  // Largest noble gas strictly below z.
  const core = [...NOBLE_GASES].reverse().find((ng) => ng.z < z) ?? null;
  if (!core) return { core: null, valence: fillsToNotation(fills) };

  const coreFills = aufbauFill(core.z);
  const coreLabels = new Set(coreFills.map((f) => f.label));
  const valence = fills.filter((f) => !coreLabels.has(f.label));
  return { core: `[${core.symbol}]`, valence: fillsToNotation(valence) };
}

// Block derivation for any Z (s/p/d/f) from the last-filled subshell. Used as a
// fallback for Z > 54 where PROPS has no hand-curated block.
export function deriveBlock(z: number): Block {
  const { fills } = groundStateFill(z);
  // The block is the subshell type of the highest-energy (last) electron, with
  // the convention that group-1/2 → s, 13–18 → p, and d/f for the inner blocks.
  const last = fills[fills.length - 1];
  if (!last) return 's';
  return last.sub as Block;
}

// Combined element record: shared ElementInfo + (curated or derived) props.
export interface FullElement extends ElementInfo {
  electronegativity: number | null;
  atomicRadius: number | null;
  ionizationEnergy: number | null;
  electronAffinity: number | null;
  meltingPoint: number | null;
  block: Block;
  oxidationStates: number[];
  electronConfig: string; // spdf notation
  shorthand: string; // noble-gas shorthand
}

function buildFull(e: ElementInfo): FullElement {
  const p = PROPS[e.z];
  const { core, valence } = nobleGasShorthand(e.z);
  return {
    ...e,
    electronegativity: p?.electronegativity ?? null,
    atomicRadius: p?.atomicRadius ?? null,
    ionizationEnergy: p?.ionizationEnergy ?? null,
    electronAffinity: p?.electronAffinity ?? null,
    meltingPoint: p?.meltingPoint ?? null,
    block: p?.block ?? deriveBlock(e.z),
    oxidationStates: p?.oxidationStates ?? [],
    electronConfig: fillsToNotation(groundStateFill(e.z).fills),
    shorthand: core ? `${core} ${valence}` : valence,
  };
}

export const FULL_ELEMENTS: FullElement[] = ELEMENTS.map(buildFull);
export const FULL_BY_Z: Record<number, FullElement> = Object.fromEntries(
  FULL_ELEMENTS.map((e) => [e.z, e]),
);
export const FULL_BY_SYMBOL: Record<string, FullElement> = Object.fromEntries(
  FULL_ELEMENTS.map((e) => [e.symbol.toLowerCase(), e]),
);

// Resolve a config that may name an element by symbol, name, or Z.
export function resolveElement(input: unknown): FullElement | null {
  if (input == null) return null;
  if (typeof input === 'number') return FULL_BY_Z[input] ?? null;
  if (typeof input === 'string') {
    const s = input.trim();
    if (/^\d+$/.test(s)) return FULL_BY_Z[Number(s)] ?? null;
    const bySym = FULL_BY_SYMBOL[s.toLowerCase()];
    if (bySym) return bySym;
    const byName = FULL_ELEMENTS.find((e) => e.name.toLowerCase() === s.toLowerCase());
    return byName ?? null;
  }
  return null;
}

// ── Trend metadata for the periodic-table overlays ────────────────────────────

export type TrendKey =
  | 'atomicRadius'
  | 'electronegativity'
  | 'ionizationEnergy'
  | 'electronAffinity'
  | 'meltingPoint';

export interface TrendMeta {
  key: TrendKey;
  label: string;
  unit: string;
  // For electronAffinity the more-negative value is "stronger"; we store a
  // direction so the heat scale can be oriented intuitively.
  accessor: (e: FullElement) => number | null;
  // Short blurb shown under the legend explaining the periodic trend.
  blurb: string;
}

export const TRENDS: TrendMeta[] = [
  {
    key: 'atomicRadius',
    label: 'Atomic radius',
    unit: 'pm',
    accessor: (e) => e.atomicRadius,
    blurb: 'Increases down a group, decreases across a period (rising Zeff).',
  },
  {
    key: 'electronegativity',
    label: 'Electronegativity',
    unit: 'Pauling',
    accessor: (e) => e.electronegativity,
    blurb: 'Increases across a period and up a group; peaks at fluorine.',
  },
  {
    key: 'ionizationEnergy',
    label: '1st ionization energy',
    unit: 'kJ/mol',
    accessor: (e) => e.ionizationEnergy,
    blurb: 'Increases across a period, decreases down a group.',
  },
  {
    key: 'electronAffinity',
    label: 'Electron affinity',
    unit: 'kJ/mol',
    accessor: (e) => (e.electronAffinity == null ? null : -e.electronAffinity), // flip sign so "more released" = higher
    blurb: 'Energy released on gaining an electron; most negative near halogens.',
  },
  {
    key: 'meltingPoint',
    label: 'Melting point',
    unit: 'K',
    accessor: (e) => e.meltingPoint,
    blurb: 'Reflects bonding/structure; peaks in the middle of the d-block (C among nonmetals).',
  },
];

// A diverging-but-mostly-sequential heat scale that reads on light AND dark:
// cool teal (low) → amber → warm coral (high). Returns an rgb() string.
export function heatColor(t: number): string {
  // t in [0,1]; clamp.
  const x = Math.max(0, Math.min(1, t));
  // Three-stop gradient: #2BB3A3 (teal) → #F4C04E (amber) → #E0564B (coral).
  const stops = [
    { p: 0.0, c: [43, 179, 163] },
    { p: 0.5, c: [244, 192, 78] },
    { p: 1.0, c: [224, 86, 75] },
  ];
  let lo = stops[0];
  let hi = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (x >= stops[i].p && x <= stops[i + 1].p) {
      lo = stops[i];
      hi = stops[i + 1];
      break;
    }
  }
  const span = hi.p - lo.p || 1;
  const f = (x - lo.p) / span;
  const ch = (i: number) => Math.round(lo.c[i] + (hi.c[i] - lo.c[i]) * f);
  return `rgb(${ch(0)}, ${ch(1)}, ${ch(2)})`;
}

// Stability hint for an isotope, by neutron/proton ratio relative to the rough
// "band of stability" used in intro courses. Purely a teaching heuristic.
export function stabilityHint(protons: number, neutrons: number): {
  level: 'stable' | 'likely' | 'unstable';
  note: string;
} {
  if (protons <= 0) return { level: 'unstable', note: 'No protons — not an atom.' };
  const ratio = neutrons / protons;
  // Light elements favour ~1:1, heavier drift toward ~1.5:1.
  const ideal = protons <= 20 ? 1.0 : 1.0 + (protons - 20) * (0.5 / 60);
  const dev = Math.abs(ratio - ideal);
  if (protons > 83) {
    return { level: 'unstable', note: 'All isotopes with Z > 83 are radioactive.' };
  }
  if (dev < 0.12) return { level: 'stable', note: 'n/p ratio sits in the band of stability.' };
  if (dev < 0.3) return { level: 'likely', note: 'Near the band of stability — may be radioactive.' };
  return {
    level: 'unstable',
    note: ratio > ideal ? 'Neutron-rich — likely β⁻ decay.' : 'Proton-rich — likely β⁺ / EC.',
  };
}

// Re-export for convenience so widgets can import everything from one module.
export { BY_Z, type ElementCategory };
